import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import CostGapDialog from "../components/CostGapDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import { base44 } from "api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import { Plus, X, Pencil, Trash2, AlertTriangle, ChevronDown, ChevronUp, Percent, Search } from "lucide-react";
import { Badge } from "components/ui/badge";
import { subDays, isAfter } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import {
  convertQuantityToInventoryUnit,
  DEFAULT_EFFORT_CLASSES,
  DEFAULT_MENU_CATEGORIES,
  formatIngredientAmount,
  getCategoryColorClass,
  getIngredientStateFromAmount,
  getDefaultMenuCategory,
  getMenuCategories,
  buildTaskCostMap,
  calculateAllocatedTaskLaborCost,
  countRecipeUnitsSold,
  normalizeIngredientForForm,
  parseLocaleNumber,
  sanitizeIngredientForSave,
} from "utils";

const OUTPUT_STOCK_UNIT_OPTIONS = ["Stk.", "kg", "g", "L", "ml", "Portion"];

const FORECAST_WEEKDAY_OPTIONS = [
  { value: 0, key: "mon" },
  { value: 1, key: "tue" },
  { value: 2, key: "wed" },
  { value: 3, key: "thu" },
  { value: 4, key: "fri" },
  { value: 5, key: "sat" },
  { value: 6, key: "sun" },
];

const DATE_RANGES = [
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "90d", days: 90 },
  { key: "all", days: null },
];

function getCategoryColor(category) {
  return getCategoryColorClass(category);
}

function hasProductDataGap(product) {
  const price = parseLocaleNumber(product?.price);
  return product?.price_missing === true
    || !Number.isFinite(price)
    || price <= 0
    || (Array.isArray(product?.cost_gaps) && product.cost_gaps.length > 0);
}

// ─── Margin bar component ────────────────────────────────────────────
function MarginBar({ pct, hasLabor }) {
  if (pct === null || pct === undefined || isNaN(pct)) {
    return <span className="text-xs text-slate-400">--</span>;
  }
  const clamped = Math.max(0, Math.min(100, pct));
  const color =
    pct >= 60
      ? "bg-emerald-500"
      : pct >= 30
        ? "bg-amber-400"
        : "bg-red-500";
  const textColor =
    pct >= 60
      ? "text-emerald-700"
      : pct >= 30
        ? "text-amber-700"
        : "text-red-600";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${clamped}%` }} />
      </div>
      <span className={`text-xs font-semibold tabular-nums w-10 text-right ${textColor}`}>
        {pct.toFixed(0)}%
      </span>
      {!hasLabor && (
        <span className="text-[10px] text-slate-400 leading-none" title="Excluding labor cost">*</span>
      )}
    </div>
  );
}

// ─── Cost Waterfall (expanded detail) ────────────────────────────────
function CostWaterfall({ price, material, labor, hasLabor }) {
  if (!price || price === 0) return null;
  const margin = price - material - labor;
  const pctMaterial = (material / price) * 100;
  const pctLabor = (labor / price) * 100;
  const pctMargin = (margin / price) * 100;

  const rows = [
    { label: "Price", value: price, pct: 100, color: "bg-slate-200" },
    { label: "Material", value: -material, pct: pctMaterial, color: "bg-red-300" },
    ...(hasLabor ? [{ label: "Labor", value: -labor, pct: pctLabor, color: "bg-orange-300" }] : []),
    {
      label: "Margin",
      value: margin,
      pct: Math.max(0, pctMargin),
      color: pctMargin >= 60 ? "bg-emerald-400" : pctMargin >= 30 ? "bg-amber-300" : "bg-red-400",
    },
  ];

  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-3 text-xs">
          <span className="w-16 text-slate-500 text-right">{r.label}</span>
          <span className="w-16 font-medium text-slate-700 tabular-nums text-right">
            {r.value >= 0 ? "" : "-"}EUR {Math.abs(r.value).toFixed(2)}
          </span>
          <div className="flex-1 h-3 bg-slate-50 rounded overflow-hidden">
            <div
              className={`h-full rounded ${r.color}`}
              style={{ width: `${Math.max(0, Math.min(100, r.pct))}%` }}
            />
          </div>
          <span className="w-10 text-slate-400 text-right tabular-nums">{r.pct.toFixed(0)}%</span>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Main Products Component
// ═══════════════════════════════════════════════════════════════════════
export default function Products() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [deleteRecipeId, setDeleteRecipeId] = useState(null);
  const [inlineDelete, setInlineDelete] = useState(null);
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [dateRange, setDateRange] = useState("30d");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [productSearch, setProductSearch] = useState("");
  const [menuFilter, setMenuFilter] = useState("on_menu");
  const [costGapFilter, setCostGapFilter] = useState(false);
  const [gapDialogRecipe, setGapDialogRecipe] = useState(null);
  const [sortKey, setSortKey] = useState("profit");
  const [sortDir, setSortDir] = useState("desc");
  const [newRecipe, setNewRecipe] = useState({
    name: "",
    category: DEFAULT_MENU_CATEGORIES[0],
    effort_class: "Other",
    servings: 1,
    ingredients: [],
    prep_time: "",
    estimated_service_minutes_per_unit: "",
    service_hourly_rate: "",
    price: "",
    active: true,
    forecast_weekdays: [],
    creates_inventory_item: false,
    output_inventory_unit: "",
  });
  const [laborAllocForm, setLaborAllocForm] = useState({ task_id: "", percentage: "" });

  // ── Queries ────────────────────────────────────────────────────────
  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ["recipes"],
    queryFn: () => base44.entities.Recipe.list(),
    initialData: [],
  });

  const { data: inventory = [] } = useQuery({
    queryKey: ["inventory"],
    queryFn: () => base44.entities.InventoryItem.list(),
    initialData: [],
  });

  const { data: sales = [] } = useQuery({
    queryKey: ["sales"],
    queryFn: () => base44.entities.Sale.list("-date"),
    initialData: [],
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list("-due_date"),
    initialData: [],
  });

  const { data: restaurant } = useQuery({
    queryKey: ["restaurant"],
    queryFn: async () => {
      const data = await base44.entities.Restaurant.list();
      return data[0] || null;
    },
  });

  const { data: recipeAllocations = [], refetch: refetchRecipeAllocations } = useQuery({
    queryKey: ["recipe-allocations", editingRecipe?.id],
    queryFn: () => base44.taskRecipeAllocations.listByRecipe(editingRecipe.id),
    initialData: [],
    enabled: !!editingRecipe?.id,
  });

  const menuCategories = useMemo(
    () => getMenuCategories(restaurant, recipes.map((recipe) => recipe.category)),
    [restaurant, recipes],
  );
  const defaultCategory = getDefaultMenuCategory(menuCategories);

  // ── Mutations ──────────────────────────────────────────────────────
  const createRecipeMutation = useMutation({
    mutationFn: (data) => base44.entities.Recipe.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recipes"] });
      closeDialog();
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const updateRecipeMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Recipe.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recipes"] });
      closeDialog();
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const deleteRecipeMutation = useMutation({
    mutationFn: (id) => base44.entities.Recipe.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recipes"] });
      setDeleteRecipeId(null);
      if (selectedRecipeId === deleteRecipeId) setSelectedRecipeId(null);
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message || "Delete failed");
      setDeleteRecipeId(null);
    },
  });

  const createLaborAllocMutation = useMutation({
    mutationFn: (data) => base44.taskRecipeAllocations.create(data),
    onSuccess: () => {
      refetchRecipeAllocations();
      queryClient.invalidateQueries({ queryKey: ["recipes"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setLaborAllocForm({ task_id: "", percentage: "" });
    },
    onError: (err) => {
      const detail = err?.response?.data?.non_field_errors?.[0] || err?.response?.data?.detail || err.message;
      alert(detail);
    },
  });

  const deleteLaborAllocMutation = useMutation({
    mutationFn: (id) => base44.taskRecipeAllocations.delete(id),
    onSuccess: () => {
      refetchRecipeAllocations();
      queryClient.invalidateQueries({ queryKey: ["recipes"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (err) => alert(err?.response?.data?.detail || err.message),
  });

  // ── Dialog helpers ─────────────────────────────────────────────────
  const resetForm = () => {
    setNewRecipe({
      name: "",
      category: defaultCategory,
      effort_class: "Other",
      servings: 1,
      ingredients: [],
      prep_time: "",
      estimated_service_minutes_per_unit: "",
      service_hourly_rate: "",
      price: "",
      active: true,
      forecast_weekdays: [],
      creates_inventory_item: false,
      output_inventory_unit: "",
    });
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingRecipe(null);
    setLaborAllocForm({ task_id: "", percentage: "" });
    resetForm();
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setNewRecipe({
      name: recipe.name || "",
      category: recipe.category || defaultCategory,
      effort_class: recipe.effort_class || "Other",
      servings: recipe.servings || 1,
      ingredients: (recipe.ingredients || []).map(normalizeIngredientForForm),
      prep_time: recipe.prep_time?.toString() || "",
      estimated_service_minutes_per_unit: recipe.estimated_service_minutes_per_unit?.toString() || "",
      service_hourly_rate: recipe.service_hourly_rate?.toString() || "",
      price: recipe.price?.toString() || "",
      active: recipe.active !== undefined ? recipe.active : true,
      forecast_weekdays: Array.isArray(recipe.forecast_weekdays) ? recipe.forecast_weekdays : [],
      creates_inventory_item: recipe.creates_inventory_item || false,
      output_inventory_unit: recipe.output_inventory_unit || "",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const recipeData = {
      ...newRecipe,
      ingredients: newRecipe.ingredients.map(sanitizeIngredientForSave),
      servings: parseLocaleNumber(newRecipe.servings) || 1,
      prep_time: newRecipe.prep_time ? parseLocaleNumber(newRecipe.prep_time) : undefined,
      estimated_service_minutes_per_unit: newRecipe.estimated_service_minutes_per_unit
        ? parseLocaleNumber(newRecipe.estimated_service_minutes_per_unit)
        : null,
      service_hourly_rate: newRecipe.service_hourly_rate ? parseLocaleNumber(newRecipe.service_hourly_rate) : null,
      price: newRecipe.price ? parseLocaleNumber(newRecipe.price) : undefined,
    };
    if (editingRecipe) {
      updateRecipeMutation.mutate({ id: editingRecipe.id, data: recipeData });
    } else {
      createRecipeMutation.mutate(recipeData);
    }
  };

  const toggleForecastWeekday = (day) => {
    const current = Array.isArray(newRecipe.forecast_weekdays) ? newRecipe.forecast_weekdays : [];
    const next = current.includes(day)
      ? current.filter((value) => value !== day)
      : [...current, day].sort((a, b) => a - b);
    setNewRecipe({ ...newRecipe, forecast_weekdays: next });
  };

  const addIngredient = () => {
    setNewRecipe({
      ...newRecipe,
      ingredients: [...newRecipe.ingredients, { inventory_item_name: "", quantity_needed: "", unit: "", amount: "" }],
    });
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...newRecipe.ingredients];
    if (field === "amount") {
      updated[index] = {
        ...updated[index],
        ...getIngredientStateFromAmount(value, updated[index].unit),
      };
    } else {
      updated[index][field] = value;
    }
    if (field === "inventory_item_name") {
      const inv = inventory.find((item) => item.name === value);
      if (inv) {
        updated[index].inventory_item_id = inv.id;
        if (!updated[index].unit) {
          updated[index].unit = inv.unit;
        }
        updated[index].amount = formatIngredientAmount(updated[index].quantity_needed, updated[index].unit);
      } else {
        updated[index].inventory_item_id = undefined;
      }
    }
    setNewRecipe({ ...newRecipe, ingredients: updated });
  };

  const removeIngredient = (index) => {
    setNewRecipe({
      ...newRecipe,
      ingredients: newRecipe.ingredients.filter((_, i) => i !== index),
    });
  };

  const confirmInlineDelete = () => {
    if (!inlineDelete) return;
    if (inlineDelete.type === "ingredient") {
      removeIngredient(inlineDelete.index);
    } else if (inlineDelete.type === "laborAllocation") {
      deleteLaborAllocMutation.mutate(inlineDelete.id);
    }
    setInlineDelete(null);
  };

  // ── Task cost map: template task_id -> estimated cost or tracked avg cost
  const taskCostMap = useMemo(() => {
    return buildTaskCostMap(tasks);
  }, [tasks]);

  // ── Filter sales by date range ─────────────────────────────────────
  const filteredSales = useMemo(() => {
    const range = DATE_RANGES.find((r) => r.key === dateRange);
    if (!range?.days) return sales;
    const cutoff = subDays(new Date(), range.days);
    return sales.filter((s) => isAfter(new Date(s.date), cutoff));
  }, [sales, dateRange]);

  // ── Compute full product data ──────────────────────────────────────
  const productRows = useMemo(() => {
    return recipes.map((recipe) => {
      const servings = parseLocaleNumber(recipe.servings) || 1;
      const price = parseLocaleNumber(recipe.price) || 0;

      // Material cost per serving
      let materialTotal = 0;
      const ingredientDetails = [];
      if (recipe.ingredients && recipe.ingredients.length > 0) {
        for (const ing of recipe.ingredients) {
          const invItem = inventory.find(
            (item) => item.name === ing.inventory_item_name || item.id === ing.inventory_item_id
          );
          const qty = parseLocaleNumber(ing.quantity_needed) || 0;
          const costPerUnit = invItem ? parseLocaleNumber(invItem.cost_per_unit) || 0 : 0;
          const convertedQty = convertQuantityToInventoryUnit(qty, ing.unit, invItem?.unit);
          const ingCost = convertedQty * costPerUnit;
          materialTotal += ingCost;
          ingredientDetails.push({
            name: ing.inventory_item_name || "Unknown",
            qty,
            unit: ing.unit || invItem?.unit || "",
            inventoryUnit: invItem?.unit || "",
            costPerUnit,
            totalCost: ingCost,
          });
        }
      }
      const materialPerServing = materialTotal / servings;

      // Labor cost per serving from task allocations
      let laborTotal = 0;
      let hasLabor = false;
      const laborDetails = [];
      if (recipe.task_allocations && recipe.task_allocations.length > 0) {
        for (const alloc of recipe.task_allocations) {
          const taskId = alloc.task_id;
          const avgCost = taskCostMap[taskId] || 0;
          const pct = parseLocaleNumber(alloc.percentage) || 0;
          const allocCost = avgCost * (pct / 100);
          if (avgCost > 0) hasLabor = true;
          laborDetails.push({
            title: alloc.task_title || `Task #${taskId}`,
            percentage: pct,
            cost: allocCost,
          });
        }
      }
      laborTotal = calculateAllocatedTaskLaborCost(recipe.task_allocations || [], taskCostMap);
      const taskLaborPerServing = laborTotal / servings;
      const serviceMinutesPerUnit = parseLocaleNumber(recipe.estimated_service_minutes_per_unit) || 0;
      const serviceHourlyRate = parseLocaleNumber(recipe.service_hourly_rate) || 0;
      const serviceLaborPerServing = serviceMinutesPerUnit > 0 && serviceHourlyRate > 0
        ? (serviceMinutesPerUnit / 60) * serviceHourlyRate
        : 0;
      if (serviceLaborPerServing > 0) {
        hasLabor = true;
        laborDetails.push({
          title: "Service per unit",
          percentage: null,
          cost: serviceLaborPerServing,
        });
      }
      const laborPerServing = taskLaborPerServing + serviceLaborPerServing;

      // Margin
      const marginPerServing = price - materialPerServing - laborPerServing;
      const marginPct = price > 0 ? (marginPerServing / price) * 100 : null;

      const unitsSold = countRecipeUnitsSold(recipe, filteredSales);

      // Total profit
      const totalProfit = marginPerServing * unitsSold;
      const totalRevenue = price * unitsSold;

      return {
        id: recipe.id,
        name: recipe.name,
        category: recipe.category,
        active: recipe.active,
        cost_gaps: recipe.cost_gaps || [],
        price_missing: recipe.price_missing === true || price <= 0,
        price,
        materialPerServing,
        laborPerServing,
        taskLaborPerServing,
        serviceLaborPerServing,
        serviceMinutesPerUnit,
        serviceHourlyRate,
        hasLabor,
        marginPerServing,
        marginPct,
        unitsSold,
        totalProfit,
        totalRevenue,
        servings,
        ingredients: recipe.ingredients || [],
        ingredientDetails,
        laborDetails,
        prepTime: recipe.prep_time,
        _recipe: recipe,
      };
    });
  }, [recipes, inventory, taskCostMap, filteredSales]);

  // ── Extract categories ─────────────────────────────────────────────
  const categories = useMemo(() => {
    const cats = new Set(recipes.map((r) => r.category).filter(Boolean));
    return ["All", ...Array.from(cats).sort()];
  }, [recipes]);

  // ── Filter + sort ──────────────────────────────────────────────────
  const filteredAndSorted = useMemo(() => {
    let rows = productRows;
    rows = rows.filter((r) =>
      menuFilter === "on_menu" ? r.active !== false : r.active === false
    );
    if (categoryFilter !== "All") {
      rows = rows.filter((r) => r.category === categoryFilter);
    }
    if (costGapFilter) {
      rows = rows.filter(hasProductDataGap);
    }
    const q = productSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const name = (r.name || "").toLowerCase();
        const category = (r.category || "").toLowerCase();
        return name.includes(q) || category.includes(q);
      });
    }

    const sorted = [...rows].sort((a, b) => {
      let va, vb;
      switch (sortKey) {
        case "name":
          va = a.name.toLowerCase();
          vb = b.name.toLowerCase();
          return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        case "price": va = a.price; vb = b.price; break;
        case "material": va = a.materialPerServing; vb = b.materialPerServing; break;
        case "labor": va = a.laborPerServing; vb = b.laborPerServing; break;
        case "margin": va = a.marginPct ?? -999; vb = b.marginPct ?? -999; break;
        case "sold": va = a.unitsSold; vb = b.unitsSold; break;
        case "profit": va = a.totalProfit; vb = b.totalProfit; break;
        default: return 0;
      }
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return sorted;
  }, [productRows, categoryFilter, menuFilter, costGapFilter, productSearch, sortKey, sortDir]);

  const productsWithCostGapsInTab = useMemo(() => {
    return productRows.filter(
      (r) =>
        (menuFilter === "on_menu" ? r.active !== false : r.active === false) &&
        hasProductDataGap(r)
    );
  }, [productRows, menuFilter]);

  // ── Portfolio summary ──────────────────────────────────────────────
  const summary = useMemo(() => {
    const menuProducts = productRows.filter(
      (r) => r.active !== false && r.price > 0 && r.marginPct !== null
    );
    const withSales = menuProducts.filter((r) => r.unitsSold > 0);
    const hasProductSales = withSales.length > 0;

    // Use sold-product weighting when product-level sales exist. Some POS imports
    // only provide category-level sales, so fall back to product margin data.
    const totalUnits = withSales.reduce((s, r) => s + r.unitsSold, 0);
    const weightedMargin =
      totalUnits > 0
        ? withSales.reduce((s, r) => s + (r.marginPct || 0) * r.unitsSold, 0) / totalUnits
        : menuProducts.length > 0
          ? menuProducts.reduce((s, r) => s + (r.marginPct || 0), 0) / menuProducts.length
          : null;

    // Top performer by realized profit when possible, otherwise by per-unit margin.
    const sorted = [...(hasProductSales ? withSales : menuProducts)].sort((a, b) =>
      hasProductSales
        ? b.totalProfit - a.totalProfit
        : b.marginPerServing - a.marginPerServing
    );
    const topPerformer = sorted.length > 0 ? sorted[0] : null;

    const alerts = menuProducts.filter(
      (r) => r.marginPct !== null && r.marginPct < 30 && (!hasProductSales || r.unitsSold > 10)
    ).length;

    return { weightedMargin, topPerformer, alerts, hasProductSales };
  }, [productRows]);

  // ── Sort handler ───────────────────────────────────────────────────
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortHeader = ({ col, children, className }) => (
    <TableHead
      className={`cursor-pointer select-none hover:text-blue-600 transition-colors text-xs ${className || ""}`}
      onClick={() => handleSort(col)}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortKey === col && <span className="text-blue-600">{sortDir === "asc" ? "^" : "v"}</span>}
      </span>
    </TableHead>
  );

  // ── Expanded product detail ────────────────────────────────────────
  const selectedProduct = useMemo(
    () => productRows.find((r) => r.id === selectedRecipeId) || null,
    [productRows, selectedRecipeId]
  );
  const handleCreateTaskForProduct = (product) => {
    if (!product?.id) return;
    // Tasks consumes this once recipes load and opens the create modal with a
    // 100% pending allocation, so the product page does not duplicate task form state.
    navigate(`/tasks?createTaskForRecipe=${product.id}`);
  };

  return (
    <Layout>
      <div className="w-full">
        <div className="max-w-7xl mx-auto p-3 sm:p-6">
          {/* ── Header ──────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t("layout.products")}</h1>
              <p className="text-slate-500 mt-0.5 text-sm">{t("products.subtitle")}</p>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 shadow-md"
              onClick={() => setIsDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("recipes.addRecipe")}
            </Button>
          </div>

          {/* ── Section 1: Portfolio Summary ─────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 sm:mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{t("products.avgMargin")}</p>
              <p className="text-2xl font-bold text-slate-900">
                {summary.weightedMargin !== null ? `${summary.weightedMargin.toFixed(0)}%` : "--"}
              </p>
              <p className="text-xs text-slate-400">
                {summary.hasProductSales ? t("products.volumeWeighted") : t("products.avgAcrossProducts")}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{t("products.topPerformer")}</p>
              {summary.topPerformer ? (
                <>
                  <p className="text-lg font-bold text-slate-900 truncate">{summary.topPerformer.name}</p>
                  <p className="text-xs text-slate-400">
                    {summary.hasProductSales
                      ? `EUR ${summary.topPerformer.totalProfit.toFixed(0)} ${t("products.totalProfit")}`
                      : `EUR ${summary.topPerformer.marginPerServing.toFixed(2)} ${t("products.marginPerUnit")}`}
                  </p>
                </>
              ) : (
                <p className="text-lg font-bold text-slate-400">--</p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{t("products.marginAlerts")}</p>
              <p className={`text-2xl font-bold ${summary.alerts > 0 ? "text-red-600" : "text-emerald-600"}`}>
                {summary.alerts}
              </p>
              <p className="text-xs text-slate-400">
                {summary.hasProductSales ? t("products.marginAlertsDesc") : t("products.marginAlertsNoSalesDesc")}
              </p>
            </div>
          </div>

          {productsWithCostGapsInTab.length > 0 && (
            <div className="mb-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <div className="flex-1">
                {productsWithCostGapsInTab.length === 1
                  ? t("recipes.dataGapBannerOne")
                  : t("recipes.dataGapBannerMany", { count: productsWithCostGapsInTab.length })}
              </div>
              <button
                type="button"
                onClick={() => setCostGapFilter((v) => !v)}
                className="rounded-md bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700"
              >
                {costGapFilter ? t("recipes.costGapShowAll") : t("recipes.costGapShowOnly")}
              </button>
            </div>
          )}

          {/* ── On-menu vs Preps toggle ───────────────────────── */}
          {(() => {
            const onMenuCount = productRows.filter((r) => r.active !== false).length;
            const prepsCount = productRows.filter((r) => r.active === false).length;
            return (
              <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setMenuFilter("on_menu")}
                  className={`flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition ${
                    menuFilter === "on_menu" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span>{t("recipes.tabOnMenu", "On menu")}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${
                    menuFilter === "on_menu" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                  }`}>{onMenuCount}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMenuFilter("preps")}
                  className={`flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition ${
                    menuFilter === "preps" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span>{t("recipes.tabPreps", "Preps & variants")}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${
                    menuFilter === "preps" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                  }`}>{prepsCount}</span>
                </button>
              </div>
            );
          })()}

          {/* ── Filters: search + category dropdown + date range ────────────── */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  type="search"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder={t('products.searchPlaceholder')}
                  className="pl-9 bg-white shadow-sm"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-56 bg-white shadow-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat === "All" ? t("products.allCategories") : cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1.5">
              {DATE_RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setDateRange(r.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    dateRange === r.key
                      ? "bg-slate-800 text-white"
                      : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {t(`products.range_${r.key}`)}
                </button>
              ))}
            </div>
          </div>

          {/* ── Section 2: Product Table ──────────────────────── */}
          <Card className="border-0 shadow-md">
            <CardContent className="p-0">
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <SortHeader col="name">{t("products.colProduct")}</SortHeader>
                      <SortHeader col="price" className="text-right">{t("products.colPrice")}</SortHeader>
                      <SortHeader col="material" className="text-right">{t("products.colMaterial")}</SortHeader>
                      <SortHeader col="labor" className="text-right">{t("products.colLabor")}</SortHeader>
                      <SortHeader col="margin">{t("products.colMargin")}</SortHeader>
                      <SortHeader col="sold" className="text-right">{t("products.colSold")}</SortHeader>
                      <SortHeader col="profit" className="text-right">{t("products.colProfit")}</SortHeader>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                          {t("recipes.loading")}
                        </TableCell>
                      </TableRow>
                    ) : filteredAndSorted.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                          {t("recipes.noRecipes")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAndSorted.map((row) => {
                        const isExpanded = selectedRecipeId === row.id;
                        return (
                          <React.Fragment key={row.id}>
                            {/* Main row */}
                            <TableRow
                              className={`cursor-pointer transition-colors ${
                                isExpanded ? "bg-blue-50" : "hover:bg-slate-50"
                              } group`}
                              onClick={() =>
                                setSelectedRecipeId(isExpanded ? null : row.id)
                              }
                            >
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="min-w-0">
                                    <span className="font-medium text-slate-900">{row.name}</span>
                                    <Badge className={`ml-2 text-[10px] ${getCategoryColor(row.category)}`}>
                                      {row.category}
                                    </Badge>
                                    {!row.active && (
                                      <Badge className="ml-2 border border-slate-200 bg-slate-100 text-[10px] text-slate-600">
                                        Not on menu
                                      </Badge>
                                    )}
                                    {row.price_missing && (
                                      <Badge className="ml-2 border border-red-200 bg-red-50 text-[10px] text-red-700">
                                        {t('recipes.missingPriceBadge')}
                                      </Badge>
                                    )}
                                    {Array.isArray(row.cost_gaps) && row.cost_gaps.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setGapDialogRecipe(row._recipe); }}
                                        title={row.cost_gaps
                                          .map((g) => `${g.name || '?'} — ${g.kind === 'unlinked' ? t('recipes.costGapTooltipUnlinked') : t('recipes.costGapTooltipNoCost')}`)
                                          .join('\n')}
                                        className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100"
                                      >
                                        <AlertTriangle className="h-3 w-3" />
                                        {row.cost_gaps.length} {t('recipes.costGapBadge')}
                                      </button>
                                    )}
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUp className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                  ) : (
                                    <ChevronDown className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-semibold tabular-nums">
                                {row.price > 0 ? `EUR ${row.price.toFixed(2)}` : t("recipes.noPriceSet")}
                              </TableCell>
                              <TableCell className="text-right text-sm text-slate-600 tabular-nums">
                                {row.materialPerServing > 0
                                  ? `EUR ${row.materialPerServing.toFixed(2)}`
                                  : "--"}
                              </TableCell>
                              <TableCell className="text-right text-sm tabular-nums">
                                {row.hasLabor ? (
                                  <span className="text-slate-600">
                                    EUR {row.laborPerServing.toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="text-slate-400" title={t("products.noLaborData")}>
                                    --
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <MarginBar pct={row.marginPct} hasLabor={row.hasLabor} />
                              </TableCell>
                              <TableCell className="text-right font-semibold tabular-nums text-slate-700">
                                {row.unitsSold > 0 ? row.unitsSold.toFixed(0) : "--"}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {row.unitsSold > 0 ? (
                                  <span className={`font-semibold ${row.totalProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                    EUR {row.totalProfit.toFixed(0)}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">--</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div
                                  className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-slate-400 hover:text-blue-600"
                                    onClick={() => handleEdit(row._recipe)}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-slate-400 hover:text-red-600"
                                    onClick={() => setDeleteRecipeId(row.id)}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>

                            {/* Section 3: Expandable detail row */}
                            {isExpanded && selectedProduct && (
                              <TableRow>
                                <TableCell colSpan={8} className="p-0 bg-slate-50/80">
                                  <div className="p-5 border-t border-slate-200">
                                    <div className="grid lg:grid-cols-2 gap-6">
                                      {/* Left: Cost waterfall + ingredient/labor lists */}
                                      <div className="space-y-5">
                                        <div>
                                          <h4 className="text-sm font-semibold text-slate-700 mb-3">
                                            {t("products.costBreakdown")}
                                          </h4>
                                          <CostWaterfall
                                            price={selectedProduct.price}
                                            material={selectedProduct.materialPerServing}
                                            labor={selectedProduct.hasLabor ? selectedProduct.laborPerServing : 0}
                                            hasLabor={selectedProduct.hasLabor}
                                          />
                                          {!selectedProduct.hasLabor && (
                                            <p className="text-[11px] text-slate-400 mt-2 italic">
                                              * {t("products.marginExclLabor")}
                                            </p>
                                          )}
                                        </div>

                                        {/* Ingredient list */}
                                        {selectedProduct.ingredientDetails.length > 0 && (
                                          <div>
                                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                              {t("recipes.ingredients")}
                                            </h4>
                                            <div className="space-y-1">
                                              {selectedProduct.ingredientDetails.map((ing, i) => (
                                                <div
                                                  key={i}
                                                  className="flex items-center justify-between text-xs text-slate-600"
                                                >
                                                  <span>
                                                    {ing.name}{" "}
                                                    <span className="text-slate-400">
                                                      {ing.qty}{ing.unit ? ` ${ing.unit}` : ""}
                                                      {ing.costPerUnit > 0
                                                        ? ` x EUR ${ing.costPerUnit.toFixed(3)}${ing.inventoryUnit ? `/${ing.inventoryUnit}` : ""}`
                                                        : ""}
                                                    </span>
                                                  </span>
                                                  <span className="font-medium tabular-nums">
                                                    {ing.totalCost > 0 ? `EUR ${ing.totalCost.toFixed(3)}` : "--"}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Labor allocations */}
                                        <div>
                                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                            {t("products.laborAllocations")}
                                          </h4>
                                          {selectedProduct.laborDetails.length > 0 ? (
                                            <div className="space-y-1">
                                              {selectedProduct.laborDetails.map((ld, i) => (
                                                <div
                                                  key={i}
                                                  className="flex items-center justify-between text-xs text-slate-600"
                                                >
                                                  <span>
                                                    {ld.title}{" "}
                                                    {ld.percentage !== null && (
                                                      <span className="text-slate-400">
                                                        ({ld.percentage}% {t("products.allocation")})
                                                      </span>
                                                    )}
                                                  </span>
                                                  <span className="font-medium tabular-nums">
                                                    {ld.cost > 0 ? `EUR ${ld.cost.toFixed(2)}` : "--"}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="text-xs text-slate-400">
                                              No task labor linked yet.
                                            </p>
                                          )}
                                        </div>
                                      </div>

                                      {/* Right: Sales info */}
                                      <div className="space-y-4">
                                        <h4 className="text-sm font-semibold text-slate-700">
                                          {t("products.salesInfo")}
                                        </h4>
                                        <div className="grid grid-cols-3 gap-3">
                                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                                            <p className="text-xs text-slate-500">{t("products.colSold")}</p>
                                            <p className="text-xl font-bold text-slate-900">
                                              {selectedProduct.unitsSold > 0
                                                ? selectedProduct.unitsSold.toFixed(0)
                                                : "--"}
                                            </p>
                                          </div>
                                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                                            <p className="text-xs text-slate-500">{t("products.revenue")}</p>
                                            <p className="text-xl font-bold text-slate-900">
                                              {selectedProduct.totalRevenue > 0
                                                ? `EUR ${selectedProduct.totalRevenue.toFixed(0)}`
                                                : "--"}
                                            </p>
                                          </div>
                                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                                            <p className="text-xs text-slate-500">{t("products.colProfit")}</p>
                                            <p
                                              className={`text-xl font-bold ${
                                                selectedProduct.totalProfit >= 0
                                                  ? "text-emerald-600"
                                                  : "text-red-600"
                                              }`}
                                            >
                                              {selectedProduct.unitsSold > 0
                                                ? `EUR ${selectedProduct.totalProfit.toFixed(0)}`
                                                : "--"}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                                          <p className="text-xs text-slate-500 mb-1">{t("products.perUnit")}</p>
                                          <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div className="flex justify-between">
                                              <span className="text-slate-500">{t("products.colPrice")}</span>
                                              <span className="font-medium">
                                                {selectedProduct.price > 0
                                                  ? `EUR ${selectedProduct.price.toFixed(2)}`
                                                  : t("recipes.noPriceSet")}
                                              </span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-slate-500">{t("products.colMaterial")}</span>
                                              <span className="font-medium">EUR {selectedProduct.materialPerServing.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-slate-500">{t("products.colLabor")}</span>
                                              <span className="font-medium">
                                                {selectedProduct.hasLabor
                                                  ? `EUR ${selectedProduct.laborPerServing.toFixed(2)}`
                                                  : "--"}
                                              </span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-slate-500">{t("products.colMargin")}</span>
                                              <span className={`font-semibold ${
                                                (selectedProduct.marginPct || 0) >= 60
                                                  ? "text-emerald-600"
                                                  : (selectedProduct.marginPct || 0) >= 30
                                                    ? "text-amber-600"
                                                    : "text-red-600"
                                              }`}>
                                                EUR {selectedProduct.marginPerServing.toFixed(2)}{" "}
                                                ({selectedProduct.marginPct !== null
                                                  ? `${selectedProduct.marginPct.toFixed(0)}%`
                                                  : "--"})
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: stacked card list (same data + handlers as the desktop table) */}
              <div className="md:hidden divide-y divide-slate-100">
                {isLoading ? (
                  <div className="text-center py-12 text-slate-500 text-sm">{t("recipes.loading")}</div>
                ) : filteredAndSorted.length === 0 ? (
                  <div className="text-center py-12 text-slate-500 text-sm">{t("recipes.noRecipes")}</div>
                ) : (
                  filteredAndSorted.map((row) => {
                    const isExpanded = selectedRecipeId === row.id;
                    return (
                      <div key={row.id} className={isExpanded ? "bg-blue-50" : ""}>
                        <div
                          className="flex items-start justify-between gap-3 p-3"
                          onClick={() => setSelectedRecipeId(isExpanded ? null : row.id)}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900 truncate">{row.name}</span>
                              {isExpanded ? (
                                <ChevronUp className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              <Badge className={`text-[10px] ${getCategoryColor(row.category)}`}>{row.category}</Badge>
                              {!row.active && (
                                <Badge className="border border-slate-200 bg-slate-100 text-[10px] text-slate-600">
                                  Not on menu
                                </Badge>
                              )}
                              {row.price_missing && (
                                <Badge className="border border-red-200 bg-red-50 text-[10px] text-red-700">
                                  {t('recipes.missingPriceBadge')}
                                </Badge>
                              )}
                              {Array.isArray(row.cost_gaps) && row.cost_gaps.length > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setGapDialogRecipe(row._recipe); }}
                                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100"
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  {row.cost_gaps.length} {t('recipes.costGapBadge')}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-blue-600"
                              onClick={() => handleEdit(row._recipe)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-red-600"
                              onClick={() => setDeleteRecipeId(row.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        <div
                          className="px-3 pb-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm"
                          onClick={() => setSelectedRecipeId(isExpanded ? null : row.id)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">{t("products.colPrice")}</span>
                            <span className="font-semibold tabular-nums text-slate-900">
                              {row.price > 0 ? `EUR ${row.price.toFixed(2)}` : t("recipes.noPriceSet")}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">{t("products.colSold")}</span>
                            <span className="font-semibold tabular-nums text-slate-700">
                              {row.unitsSold > 0 ? row.unitsSold.toFixed(0) : "--"}
                            </span>
                          </div>
                          <div className="col-span-2 flex items-center justify-between">
                            <span className="text-slate-500">{t("products.colMargin")}</span>
                            <MarginBar pct={row.marginPct} hasLabor={row.hasLabor} />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">{t("products.colProfit")}</span>
                            {row.unitsSold > 0 ? (
                              <span className={`font-semibold tabular-nums ${row.totalProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                EUR {row.totalProfit.toFixed(0)}
                              </span>
                            ) : (
                              <span className="text-slate-400">--</span>
                            )}
                          </div>
                        </div>

                        {isExpanded && selectedProduct && (
                          <div className="px-3 pb-4 border-t border-slate-200 bg-slate-50/80 space-y-4">
                            <div className="pt-3">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                {t("products.costBreakdown")}
                              </h4>
                              <CostWaterfall
                                price={selectedProduct.price}
                                material={selectedProduct.materialPerServing}
                                labor={selectedProduct.hasLabor ? selectedProduct.laborPerServing : 0}
                                hasLabor={selectedProduct.hasLabor}
                              />
                              {!selectedProduct.hasLabor && (
                                <p className="text-[11px] text-slate-400 mt-2 italic">
                                  * {t("products.marginExclLabor")}
                                </p>
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div className="bg-white rounded-lg p-2 border border-slate-200">
                                <p className="text-[11px] text-slate-500">{t("products.colSold")}</p>
                                <p className="text-base font-bold text-slate-900">
                                  {selectedProduct.unitsSold > 0 ? selectedProduct.unitsSold.toFixed(0) : "--"}
                                </p>
                              </div>
                              <div className="bg-white rounded-lg p-2 border border-slate-200">
                                <p className="text-[11px] text-slate-500">{t("products.revenue")}</p>
                                <p className="text-base font-bold text-slate-900">
                                  {selectedProduct.totalRevenue > 0 ? `EUR ${selectedProduct.totalRevenue.toFixed(0)}` : "--"}
                                </p>
                              </div>
                              <div className="bg-white rounded-lg p-2 border border-slate-200">
                                <p className="text-[11px] text-slate-500">{t("products.colProfit")}</p>
                                <p className={`text-base font-bold ${selectedProduct.totalProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                  {selectedProduct.unitsSold > 0 ? `EUR ${selectedProduct.totalProfit.toFixed(0)}` : "--"}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

            </CardContent>
          </Card>

          {/* ── Recipe Dialog (Add/Edit) ─────────────────────────── */}
          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              if (!open) closeDialog();
              else setIsDialogOpen(true);
            }}
          >
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingRecipe ? t("recipes.editRecipe") : t("recipes.newRecipe")}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">{t("recipes.recipeName")}</Label>
                    <Input
                      id="name"
                      value={newRecipe.name}
                      onChange={(e) => setNewRecipe({ ...newRecipe, name: e.target.value })}
                      placeholder={t("recipes.recipeNamePlaceholder")}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">{t("recipes.category")}</Label>
                    <Select
                      value={newRecipe.category}
                      onValueChange={(value) => setNewRecipe({ ...newRecipe, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {menuCategories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="effort_class">Effort class</Label>
                    <Select
                      value={newRecipe.effort_class}
                      onValueChange={(value) => setNewRecipe({ ...newRecipe, effort_class: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEFAULT_EFFORT_CLASSES.map((effortClass) => (
                          <SelectItem key={effortClass} value={effortClass}>
                            {effortClass}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="servings">{t("recipes.servings")}</Label>
                    <Input
                      id="servings"
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={newRecipe.servings}
                      onChange={(e) => setNewRecipe({ ...newRecipe, servings: e.target.value })}
                      placeholder={t("recipes.servingsPlaceholder")}
                    />
                    <p className="text-xs text-slate-500 mt-1">{t("recipes.servingsHelp")}</p>
                  </div>
                  <div>
                    <Label htmlFor="price">{t("recipes.price")}</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={newRecipe.price}
                      onChange={(e) => setNewRecipe({ ...newRecipe, price: e.target.value })}
                      placeholder={t("recipes.pricePlaceholder")}
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_time">Service time (min)</Label>
                    <Input
                      id="service_time"
                      type="number"
                      min="0"
                      step="0.1"
                      value={newRecipe.estimated_service_minutes_per_unit}
                      onChange={(e) => setNewRecipe({ ...newRecipe, estimated_service_minutes_per_unit: e.target.value })}
                      placeholder="1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_rate">Service hourly rate (€)</Label>
                    <Input
                      id="service_rate"
                      type="number"
                      min="0"
                      step="0.01"
                      value={newRecipe.service_hourly_rate}
                      onChange={(e) => setNewRecipe({ ...newRecipe, service_hourly_rate: e.target.value })}
                      placeholder="18.00"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="active"
                      checked={newRecipe.active}
                      onChange={(e) => setNewRecipe({ ...newRecipe, active: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="active" className="cursor-pointer">
                      {t("recipes.showOnCustomerMenu", "Show on customer menu")}
                    </Label>
                  </div>
                  <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label htmlFor="creates_inventory_item" className="cursor-pointer">
                          {t("recipes.createStockItem")}
                        </Label>
                        <p className="mt-1 text-xs text-slate-500">
                          {t("recipes.createStockItemDesc")}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        id="creates_inventory_item"
                        checked={newRecipe.creates_inventory_item}
                        onChange={(e) =>
                          setNewRecipe({
                            ...newRecipe,
                            creates_inventory_item: e.target.checked,
                            output_inventory_unit: e.target.checked ? (newRecipe.output_inventory_unit || OUTPUT_STOCK_UNIT_OPTIONS[0]) : "",
                          })
                        }
                        className="rounded"
                      />
                    </div>
                    {newRecipe.creates_inventory_item && (
                      <div className="mt-4 max-w-xs">
                        <Label htmlFor="output_inventory_unit">{t("recipes.stockUnit")}</Label>
                        <Select
                          value={newRecipe.output_inventory_unit || OUTPUT_STOCK_UNIT_OPTIONS[0]}
                          onValueChange={(value) => setNewRecipe({ ...newRecipe, output_inventory_unit: value })}
                        >
                          <SelectTrigger id="output_inventory_unit">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {OUTPUT_STOCK_UNIT_OPTIONS.map((unit) => (
                              <SelectItem key={unit} value={unit}>
                                {unit}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ingredients */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>{t("recipes.ingredients")}</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addIngredient}>
                      <Plus className="w-4 h-4 mr-1" />
                      {t("recipes.addIngredient")}
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {newRecipe.ingredients.map((ingredient, idx) => {
                      const name = ingredient.inventory_item_name;
                      const notInInventory =
                        name && !inventory.some((item) => item.name.toLowerCase() === name.toLowerCase());
                      return (
                        <div key={idx} className="grid gap-2 p-3 bg-slate-50 rounded-lg items-start md:grid-cols-[minmax(0,1fr)_160px_auto]">
                          <div className="space-y-1">
                            <Input
                              placeholder="Ingredient name"
                              className={notInInventory ? "border-amber-400 focus-visible:ring-amber-400" : ""}
                              value={name}
                              onChange={(e) => updateIngredient(idx, "inventory_item_name", e.target.value)}
                            />
                            {notInInventory && (
                              <p className="flex items-center gap-1 text-xs text-amber-600">
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                                Not in inventory -- will be saved but won't link to stock.
                              </p>
                            )}
                          </div>
                          <Input
                            type="text"
                            placeholder={t("recipes.amountPlaceholder")}
                            value={ingredient.amount || formatIngredientAmount(ingredient.quantity_needed, ingredient.unit)}
                            onChange={(e) => updateIngredient(idx, "amount", e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="mt-0.5 justify-self-end"
                            onClick={() => setInlineDelete({
                              type: "ingredient",
                              index: idx,
                              title: "Remove ingredient?",
                              description: "This ingredient will be removed from the recipe form. The change is saved when you update the recipe.",
                            })}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                    {newRecipe.ingredients.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">
                        {t("recipes.noIngredientsAdded")}
                      </p>
                    )}
                  </div>
                </div>

                {/* Labor Allocations — only shown when editing an existing recipe */}
                {editingRecipe && (() => {
                  const templateTasks = Array.isArray(tasks) ? tasks.filter((task) => task.is_template) : [];
                  const totalPct = Array.isArray(recipeAllocations)
                    ? recipeAllocations.reduce((s, a) => s + parseLocaleNumber(a.percentage || 0), 0)
                    : 0;
                  const handleAddLaborAlloc = () => {
                    if (!laborAllocForm.task_id || !laborAllocForm.percentage) return;
                    createLaborAllocMutation.mutate({
                      task_id: parseInt(laborAllocForm.task_id, 10),
                      recipe_id: editingRecipe.id,
                      percentage: parseLocaleNumber(laborAllocForm.percentage),
                    });
                  };
                  return (
                    <div className="space-y-3 border-t pt-4">
                      <div className="flex items-center gap-2">
                        <Percent className="w-4 h-4 text-slate-500" />
                        <Label className="text-sm font-semibold text-slate-700">{t("recipes.laborAllocations")}</Label>
                        {recipeAllocations.length > 0 && (
                          <span className="text-xs text-slate-500">{t("recipes.laborAllocationsTotal")}: {totalPct.toFixed(0)}%</span>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="ml-auto h-8 text-xs"
                          onClick={() => handleCreateTaskForProduct(editingRecipe)}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Create task
                        </Button>
                      </div>
                      {recipeAllocations.length > 0 && (
                        <div className="space-y-1.5">
                          {recipeAllocations.map((a) => (
                            <div key={a.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm">
                              <span className="flex-1 text-slate-700 truncate">{a.task_title}</span>
                              <span className="font-medium text-emerald-700 w-12 text-right">{parseLocaleNumber(a.percentage).toFixed(0)}%</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-500 hover:bg-red-50"
                                onClick={() => setInlineDelete({
                                  type: "laborAllocation",
                                  id: a.id,
                                  title: "Remove labor allocation?",
                                  description: "This task allocation will be removed from the product's labor cost calculation.",
                                })}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {recipeAllocations.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-2">
                          {t("recipes.noLaborAllocations")}
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <Select
                            value={laborAllocForm.task_id}
                            onValueChange={(v) => setLaborAllocForm({ ...laborAllocForm, task_id: v })}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder={t("recipes.selectTask")} />
                            </SelectTrigger>
                            <SelectContent>
                              {templateTasks.map((tk) => (
                                <SelectItem key={tk.id} value={String(tk.id)}>{tk.title}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-20">
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            step="1"
                            className="h-8 text-sm"
                            placeholder="%"
                            value={laborAllocForm.percentage}
                            onChange={(e) => setLaborAllocForm({ ...laborAllocForm, percentage: e.target.value })}
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 bg-emerald-700 hover:bg-emerald-800"
                          disabled={!laborAllocForm.task_id || !laborAllocForm.percentage || createLaborAllocMutation.isPending}
                          onClick={handleAddLaborAlloc}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })()}

                <div className="space-y-2 border-t pt-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">
                      {t("recipes.forecastAvailability")}
                    </Label>
                    <p className="mt-1 text-xs text-slate-500">
                      {t("recipes.forecastAvailabilityDesc")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={`h-8 rounded-md border px-3 text-xs font-medium transition-colors ${
                        newRecipe.forecast_weekdays.length === 0
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      onClick={() => setNewRecipe({ ...newRecipe, forecast_weekdays: [] })}
                    >
                      {t("recipes.forecastAvailabilityEveryDay")}
                    </button>
                    {FORECAST_WEEKDAY_OPTIONS.map((day) => {
                      const selected = newRecipe.forecast_weekdays.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          className={`h-8 min-w-12 rounded-md border px-3 text-xs font-medium transition-colors ${
                            selected
                              ? "border-blue-600 bg-blue-50 text-blue-700"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                          onClick={() => toggleForecastWeekday(day.value)}
                        >
                          {t(`recipes.weekdays.${day.key}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    {t("recipes.cancel")}
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editingRecipe ? t("recipes.update") : t("recipes.save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* ── Delete Confirmation ─────────────────────────────── */}
          <AlertDialog open={!!deleteRecipeId} onOpenChange={() => setDeleteRecipeId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("recipes.deleteRecipe")}</AlertDialogTitle>
                <AlertDialogDescription>{t("recipes.deleteRecipeDesc")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("recipes.cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteRecipeMutation.mutate(deleteRecipeId)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {t("recipes.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <ConfirmDeleteDialog
            open={!!inlineDelete}
            onOpenChange={(open) => {
              if (!open) setInlineDelete(null);
            }}
            title={inlineDelete?.title}
            description={inlineDelete?.description}
            cancelLabel={t("recipes.cancel")}
            confirmLabel={t("recipes.delete")}
            onConfirm={confirmInlineDelete}
          />
          <CostGapDialog
            recipe={gapDialogRecipe}
            inventoryItems={inventory}
            recipes={recipes}
            open={!!gapDialogRecipe}
            onClose={() => setGapDialogRecipe(null)}
          />
        </div>
      </div>
    </Layout>
  );
}
